import { useEffect, useRef, useCallback } from 'react';
import { DocumentProgressDto } from 'shared/signalR/hooks/useDocumentNotifications';

/**
 * Génère un ID unique pour une notification de document
 */
const getDocumentNotificationId = (
  notification: DocumentProgressDto
): string => {
  return `${notification.storedFileId}-${notification.fileType}-${notification.replaceFileType || ''}`;
};

/**
 * Génère un ID unique pour une notification générique basé sur le contenu JSON
 */
const getGenericNotificationId = <T>(notification: T): string => {
  return JSON.stringify(notification);
};

/**
 * Hook générique pour traiter les notifications avec déduplication
 *
 * @param notifications - Liste des notifications reçues
 * @param onNewNotification - Callback appelé pour chaque nouvelle notification
 * @param getNotificationId - Fonction optionnelle pour générer l'ID unique
 */
export const useProcessedNotificationsGeneric = <T>(
  notifications: T[],
  onNewNotification: (notification: T) => void,
  getNotificationId: (notification: T) => string = getGenericNotificationId
): void => {
  const processedIds = useRef(new Set<string>());
  const onNewNotificationRef = useRef(onNewNotification);

  // Garder la référence du callback à jour sans déclencher l'effet
  useEffect(() => {
    onNewNotificationRef.current = onNewNotification;
  }, [onNewNotification]);

  useEffect(() => {
    const newNotifications = notifications.filter(notification => {
      const notificationId = getNotificationId(notification);

      // Ignorer si déjà traitée
      if (processedIds.current.has(notificationId)) {
        return false;
      }

      // Marquer comme traitée
      processedIds.current.add(notificationId);
      return true;
    });

    // Traiter les nouvelles notifications
    newNotifications.forEach(notification => {
      console.log('[ProcessedNotifications] Processing new notification:', notification);
      onNewNotificationRef.current(notification);
    });
  }, [notifications, getNotificationId]);
};

/**
 * Hook pour traiter les notifications de documents SignalR avec déduplication
 *
 * Centralise la logique de déduplication des notifications qui était
 * dupliquée dans DocumentCardContainer et BorrowerContractualizationStep
 *
 * @param notifications - Liste des notifications reçues
 * @param onNewNotification - Callback appelé pour chaque nouvelle notification
 */
export const useProcessedNotifications = (
  notifications: DocumentProgressDto[],
  onNewNotification: (notification: DocumentProgressDto) => void
): void => {
  const processedIds = useRef(new Set<string>());
  const onNewNotificationRef = useRef(onNewNotification);

  // Garder la référence du callback à jour sans déclencher l'effet
  useEffect(() => {
    onNewNotificationRef.current = onNewNotification;
  }, [onNewNotification]);

  useEffect(() => {
    const newNotifications = notifications.filter(notification => {
      const notificationId = getDocumentNotificationId(notification);

      // Ignorer si déjà traitée
      if (processedIds.current.has(notificationId)) {
        return false;
      }

      // Ignorer si pas de storedFileId
      if (notification.storedFileId === null) {
        return false;
      }

      // Marquer comme traitée
      processedIds.current.add(notificationId);
      return true;
    });

    // Traiter les nouvelles notifications
    newNotifications.forEach(notification => {
      onNewNotificationRef.current(notification);
    });
  }, [notifications]);
};

/**
 * Hook pour gérer la connexion SignalR et le traitement des notifications
 *
 * Combine useDocumentNotifications avec useProcessedNotifications
 * pour une utilisation simplifiée
 *
 * @param quoteId - ID du devis pour la connexion SignalR
 * @param onDocumentUpdate - Callback appelé pour chaque mise à jour de document
 * @param onPopInNotification - Callback optionnel pour les notifications popup
 */
export const useDocumentNotificationsWithProcessing = (
  quoteId: string | undefined,
  onDocumentUpdate: (notification: DocumentProgressDto) => void,
  onPopInNotification?: (notification: DocumentProgressDto) => void
) => {
  const {
    useDocumentNotifications
  } = require('shared/signalR/hooks/useDocumentNotifications');

  const { memoizedConnect, memoizedDisconnect, notifications } =
    useDocumentNotifications(quoteId);

  // Connecter/déconnecter SignalR
  useEffect(() => {
    memoizedConnect();
    return memoizedDisconnect;
  }, [memoizedConnect, memoizedDisconnect]);

  // Handler combiné pour les notifications
  const handleNotification = useCallback(
    (notification: DocumentProgressDto) => {
      onDocumentUpdate(notification);

      if (onPopInNotification && notification.notificationPopIn) {
        onPopInNotification(notification);
      }
    },
    [onDocumentUpdate, onPopInNotification]
  );

  // Traiter les notifications avec déduplication
  useProcessedNotifications(notifications, handleNotification);

  return {
    notifications,
    memoizedConnect,
    memoizedDisconnect
  };
};
